import { firstBy } from "thenby";
import type { CircuitMetric, Exercise, ExerciseMetric, ExerciseRepetition, ScoreMetadata } from "types";
import { ExerciseType } from "types";
import type { SortType } from "types/output-exercise";
import UnitHelper from "helpers/UnitHelper";

class ExerciseHelpers {
  static constants = {
    SLAMS_SWINGS_THROWS_CATEGORY_ID: "Slams, Swings & Throws",
    /** 1RM metrics, in the order the dashboard metric dropdown lists them. */
    ONE_REP_MAX_METRIC_FIELDS: ["lvpAutomatedOneRepMax", "weightOneRepMax", "estimatedOneRepMax"],
  };

  static isOneRepMaxMetricField(field?: string) {
    return Boolean(field && ExerciseHelpers.constants.ONE_REP_MAX_METRIC_FIELDS.includes(field));
  }

  /** Builds a synthetic 1RM metric (e.g. `weightOneRepMax`) that dashboards add to an exercise's metrics. */
  static createOneRepMaxMetric(args: Pick<ExerciseMetric, "name" | "field">): ExerciseMetric {
    return {
      field: args.field,
      name: args.name,
      sortType: 1 as SortType,
      isPbEnabled: false,
      displayMetadata: {
        imperial: {
          decimals: null,
          unit: "Pound",
        },
        metric: null,
      },
      isDefault: false,
      unitOfMeasure: UnitHelper.units.Kilogram,
    };
  }

  /** Moves 1RM metrics to the front, in `ONE_REP_MAX_METRIC_FIELDS` order; other metrics keep their order. */
  static sortOneRepMaxMetricsFirst<T extends Pick<ExerciseMetric, "field">>(metrics: T[]): T[] {
    return metrics
      .slice()
      .sort(
        firstBy((metric: T) => !ExerciseHelpers.isOneRepMaxMetricField(metric.field)).thenBy((metric: T) =>
          ExerciseHelpers.constants.ONE_REP_MAX_METRIC_FIELDS.indexOf(metric.field)
        )
      );
  }

  static is10_5Test(exercise?: Exercise) {
    return Boolean(exercise?.id?.includes("10_5"));
  }

  static canConfigureReps(exercise?: Exercise) {
    return Boolean(!ExerciseHelpers.is10_5Test(exercise) && ExerciseHelpers.isRepExercise(exercise));
  }

  static isRepExercise(exercise?: Exercise) {
    return Boolean(exercise?.repetitions?.length) || ExerciseHelpers.hasRepCountMetric(exercise);
  }

  static isVBTExercise(exercise?: Exercise) {
    if (!exercise || !exercise.categoryId) {
      return false;
    }
    return ["Barbell Velocity", "Slams, Swings & Throws"].includes(`${exercise.categoryId}`);
  }

  /**
   * Determines if an exercise is capable of LVP (Load Velocity Profile) based on its minimum velocity threshold.
   * This is a temporary check, we will add custom properties to the exercise in the future.
   */
  static isLvpCapableExercise(exercise?: Exercise) {
    if (!exercise?.categoryId) {
      return false;
    }

    return exercise.minimumVelocityThreshold !== null && exercise.minimumVelocityThreshold !== undefined;
  }

  /** A custom exercise takes its minimum velocity threshold from the exercise it was derived from. */
  static inheritMinimumVelocityThreshold<T extends Exercise>(
    exercise: T,
    getExerciseById: (id: string) => Exercise | undefined
  ): T {
    if (exercise.type !== ExerciseType.Custom || !exercise.derivedFromExerciseId) {
      return exercise;
    }

    const derivedFromExercise = getExerciseById(exercise.derivedFromExerciseId);

    return derivedFromExercise
      ? { ...exercise, minimumVelocityThreshold: derivedFromExercise.minimumVelocityThreshold }
      : exercise;
  }

  static isOneRepMaxExercise(exercise?: Exercise) {
    return Boolean(exercise?.isOneRepMaxEnabled);
  }

  static canToggleOneRepMax(exercise?: Exercise) {
    return ExerciseHelpers.isRepExercise(exercise) && ExerciseHelpers.hasWeightMetric(exercise);
  }

  static getWeightMetric(exercise?: Exercise) {
    return exercise?.metrics.find((metric) => metric.field === "weight");
  }

  static hasWeightMetric(exercise?: Exercise) {
    return Boolean(ExerciseHelpers.getWeightMetric(exercise));
  }

  static hasRepCountMetric(exercise?: Exercise) {
    return Boolean(exercise?.metrics.find((metric) => metric.field === "repCount"));
  }

  static hasVariants(exercise?: Exercise) {
    return Boolean(exercise?.variants.length);
  }

  static isPowerMetric(field?: string): boolean {
    return Boolean(field?.toUpperCase().includes("POWER"));
  }

  static velocityTargetsCalculatedByPeakOnly(exercise: Exercise): boolean {
    if (!exercise.categoryId) {
      if (process.env.NODE_ENV === "development") {
        throw new Error("Exercise categoryId is missing");
      }
      return false;
    }

    const disallowedExerciseIds = [
      "BARBELL_HANG_CLEAN",
      "BARBELL_HANG_SNATCH",
      "BARBELL_POWER_CLEAN",
      "BARBELL_POWER_SNATCH",
      "FREE_VBT_OLYMPICS",
      "BARBELL_CLEAN_PULL",
    ];

    const disallowedCategories = ["Slams, Swings & Throws"];

    const exerciseId: string = (exercise.type === "Custom" && exercise.derivedFromExerciseId) || exercise.id;

    return Boolean(disallowedExerciseIds.includes(exerciseId) || disallowedCategories.includes(exercise.categoryId));
  }

  static generateInstructions(exercise: Exercise) {
    const rowDelimiter = exercise.instructions?.indexOf("\r\n") !== -1 ? "\r\n" : "\n";

    if (!exercise.instructions?.includes("\n")) {
      return [exercise.instructions || ""];
    }

    return (exercise.instructions || "").split(rowDelimiter).map((line, index) => `${index + 1}. ${line}`);
  }

  static sort(exercises: Exercise[], compare: (a: any, b: any) => number): Exercise[] {
    return [...exercises].sort(
      firstBy("category", { cmp: compare, ignoreCase: true }).thenBy("name", { cmp: compare })
    );
  }

  /**
   * @description Method gets the metric by field name or the default metric if there is no default metric it will return the first metric
   *
   */
  static getMetricByFieldNameOrDefault(exercise: Exercise, metricField: string | undefined | null) {
    const metric =
      exercise.metrics?.find((metric) => metric.field === metricField) ||
      exercise.metrics?.find((metric) => metric.isDefault) ||
      exercise.metrics[0];

    return metric;
  }

  /**
   * @description Method filters exercises by id
   */
  static filterExercisesById(exercises: Exercise[], id: Exercise["id"]) {
    return exercises.filter((exercise) => exercise.id === id);
  }

  /**
   * @description Method filters exercises by ids
   */
  static filterExercisesByIds(exercises: Exercise[], id: Exercise["id"][]) {
    return exercises.filter((exercise) => id.includes(exercise.id));
  }

  static getBodyTarget(exerciseId: string): "UPPER" | "LOWER" | null {
    const upperBodyExerciseIds: string[] = [
      "BARBELL_BENCH_PRESS",
      "BARBELL_BENCH_PULL",
      "BARBELL_BENT_OVER_ROW",
      "BARBELL_DECLINE_BENCH_PRESS",
      "BARBELL_DOUBLE_ARM_LANDMINE_PRESS",
      "BARBELL_FLOOR_PRESS",
      "BARBELL_INCLINE_BENCH_PRESS",
      "BARBELL_LANDMINE_PRESS",
      "BARBELL_OVERHEAD_PRESS",
      "BARBELL_PENDLAY_ROW",
      "BARBELL_PUSH_PRESS",
      "DUMBBELL_BENCH_PRESS",
      "DUMBBELL_DECLINE_BENCH_PRESS",
      "DUMBBELL_INCLINE_BENCH_PRESS",
      "DUMBBELL_ROW",
      "DUMBBELL_SHOULDER_PRESS",
      "DUMBBELL_SINGLE_ARM_BENCH_PRESS",
      "DUMBBELL_SINGLE_ARM_DECLINE_BENCH_PRESS",
      "DUMBBELL_SINGLE_ARM_INCLINE_BENCH_PRESS",
      "DUMBBELL_SINGLE_ARM_SHOULDER_PRESS",
    ];

    if (upperBodyExerciseIds.includes(exerciseId)) return "UPPER";

    const lowerBodyExerciseIds: string[] = [
      "BARBELL_BACK_SQUAT",
      "BARBELL_DEADLIFT",
      "BARBELL_FRONT_SQUAT",
      "BARBELL_HIP_THRUST",
      "BARBELL_LUNGE",
      "BARBELL_RFE_SPLIT_SQUAT",
      "BARBELL_ROMANIAN_DEADLIFT",
      "BARBELL_SINGLE_LEG_HIP_THRUST",
      "BARBELL_SINGLE_LEG_ROMANIAN_DEADLIFT",
      "BARBELL_SPLIT_SQUAT",
      "BARBELL_STEP_UP",
      "BARBELL_ZERCHER_SQUAT",
      "DUMBBELL_GOBLET_SQUAT",
      "FREE_VBT_DEADLIFT",
      "SAFETY_SQUAT_BAR_SQUAT",
      "TRAP_BAR_DEADLIFT",
      "BARBELL_BOX_SQUAT",
    ];

    if (lowerBodyExerciseIds.includes(exerciseId)) return "LOWER";

    return null;
  }

  static isSpecialCaseMetric(
    metric?: ExerciseMetric | ExerciseRepetition | ScoreMetadata | CircuitMetric | null,
    imperialSystemEnabled?: boolean
  ) {
    const measurementSystem = imperialSystemEnabled ? "imperial" : "metric";
    const displayMetadataUnit = metric?.displayMetadata?.[measurementSystem]?.unit || "";

    if (!displayMetadataUnit) {
      return false;
    }

    return ["FeetInches", "Time"].includes(displayMetadataUnit);
  }

  static isUpperBodyExercise(exerciseId: Exercise["id"]): boolean {
    return ExerciseHelpers.getBodyTarget(exerciseId) === "UPPER";
  }

  static isLowerBodyExercise(exerciseId: Exercise["id"]): boolean {
    return ExerciseHelpers.getBodyTarget(exerciseId) === "LOWER";
  }
}

export default ExerciseHelpers;
